import React, { CSSProperties } from 'react';
import './CtaButton.css';
import { CtaIcon } from './icons';
import { getTextFill } from '../text/TextFill';
import {
	alignToFlex,
	getHoverProps,
	getMarginStyle,
	getPaddingStyle,
	getTextStyle,
	HoverKey,
	px,
} from '../styles';
import { ComponentSettings, TypographySettings } from '../../../types';

interface CtaButtonProps {
	settings: ComponentSettings;
	text: TypographySettings;
	link: string;
	openInNewTab: boolean;
	/** Admin preview: the link does not navigate. */
	isPreview?: boolean;
}

/**
 * Hex color with an alpha (glass style tints).
 *
 * @param hex   Color, e.g. "#00CCFF".
 * @param alpha 0–1.
 */
const withAlpha = ( hex: string | undefined, alpha: number ): string => {
	const match = /^#?([0-9a-f]{6})$/i.exec( hex || '' );
	if ( ! match ) {
		return 'transparent';
	}
	const value = parseInt( match[ 1 ], 16 );
	return `rgba(${ ( value >> 16 ) & 255 }, ${ ( value >> 8 ) & 255 }, ${
		value & 255
	}, ${ alpha })`;
};

/**
 * Call-to-action button. Variants (Figma floating bars): gradient, solid,
 * green (solid), bordered (light, dark on hover), outline, glass.
 */
const CtaButton: React.FC< CtaButtonProps > = ( {
	settings,
	text,
	link,
	openInNewTab,
	isPreview,
} ) => {
	if ( ! text.text ) {
		return null;
	}

	const { variant, style } = settings;
	// Min height (not a fixed height), so padding and line height can make
	// the button taller; 0 lets the text, line height and padding decide.
	const buttonStyle: CSSProperties = {
		...getTextStyle( text ),
		justifyContent: alignToFlex( text.align ),
		minWidth: px( style.width ),
		minHeight: px( style.height ),
		borderRadius: px( style.radius ),
		...getPaddingStyle( style ),
		...getMarginStyle( style ),
	};
	if ( variant === 'gradient' ) {
		buttonStyle.background = `linear-gradient(180deg, ${ style.gradientFrom } 0%, ${ style.gradientTo } 100%)`;
	} else if ( variant === 'solid' || variant === 'green' ) {
		buttonStyle.background = style.bgColor;
	} else if ( variant === 'bordered' ) {
		Object.assign( buttonStyle, {
			background: style.bgColor,
			borderColor: style.borderColor,
			'--sb-cta-hover-bg': style.hoverBgColor,
			'--sb-cta-hover-color': style.hoverTextColor,
		} );
	} else if ( variant === 'glass' ) {
		buttonStyle.background = `linear-gradient(106.5deg, ${ withAlpha(
			style.glassFrom,
			0.2
		) } 0.6%, ${ withAlpha( style.glassTo, 0.2 ) } 100%)`;
	} else {
		// Outline: border color; the text keeps its own color.
		buttonStyle.borderColor = style.borderColor;
	}

	// Hover colors: the bordered style has its own (always set) colors.
	const hoverKeys: HoverKey[] =
		variant === 'bordered'
			? [ 'hoverBorder' ]
			: [
					'hoverBg',
					'hoverText',
					...( variant === 'outline'
						? ( [ 'hoverBorder' ] as HoverKey[] )
						: [] ),
			  ];
	const hover = getHoverProps( style, hoverKeys );
	Object.assign( buttonStyle, hover.style );

	const hasIcon = !! style.icon && style.icon !== 'none';
	const iconLeft = style.iconPosition === 'left';
	if ( hasIcon ) {
		buttonStyle.gap = px( style.iconGap ) || '8px';
	}
	const icon = hasIcon ? (
		<CtaIcon
			name={ style.icon }
			size={ px( style.iconSize ) }
			className="sb-cta-icon"
		/>
	) : null;

	const href = link || undefined;
	const newTab = !! href && openInNewTab;

	return (
		<a
			className={ `sb-cta is-${ variant }${ hover.className }` }
			href={ href }
			target={ newTab ? '_blank' : undefined }
			rel={ newTab ? 'noopener noreferrer' : undefined }
			onClick={ ( event ) => {
				if ( isPreview ) {
					event.preventDefault();
				}
			} }
			style={ buttonStyle }
		>
			{ iconLeft && icon }
			<span className="sb-cta-label" style={ getTextFill( text ) }>
				{ text.text }
			</span>
			{ ! iconLeft && icon }
		</a>
	);
};

export default CtaButton;
